import { Check, Loader2, RotateCcw, X } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { StatusPill } from "@/components/ui/status-pill";
import { api } from "@/lib/api";
import type { EditAnnotation } from "@/lib/stream";
import { invalidatePdfBytes } from "@/hooks/useFetchPdfBytes";
import { invalidateDocxBytes } from "@/hooks/useFetchDocxBytes";

export type EditResolution = "pending" | "accepted" | "rejected";

function findByWid(root: ParentNode, kind: "ins" | "del", wId?: string | null) {
  if (!wId) {
    return null;
  }
  return root.querySelector<HTMLElement>(`[data-w-kind="${kind}"][data-w-id="${CSS.escape(wId)}"], [w\\:id="${CSS.escape(wId)}"]`);
}

export function applyOptimisticResolution(annotation: EditAnnotation, action: "accept" | "reject") {
  const delNode = findByWid(document, "del", annotation.del_w_id);
  const insNode = findByWid(document, "ins", annotation.ins_w_id);
  const previous = [
    delNode ? { node: delNode, display: delNode.style.display, opacity: delNode.style.opacity, className: delNode.className } : null,
    insNode ? { node: insNode, display: insNode.style.display, opacity: insNode.style.opacity, className: insNode.className } : null
  ].filter(Boolean) as Array<{ node: HTMLElement; display: string; opacity: string; className: string }>;

  if (action === "accept") {
    if (delNode) {
      delNode.style.display = "none";
    }
    if (insNode) {
      insNode.style.opacity = "1";
      insNode.classList.add("vadium-edit-accepted");
    }
  } else {
    if (insNode) {
      insNode.style.display = "none";
    }
    if (delNode) {
      delNode.style.opacity = "1";
      delNode.classList.add("vadium-edit-rejected");
    }
  }

  return () => {
    previous.forEach((entry) => {
      entry.node.style.display = entry.display;
      entry.node.style.opacity = entry.opacity;
      entry.node.className = entry.className;
    });
  };
}

interface EditCardProps {
  annotation: EditAnnotation;
  filename?: string;
  resolvedStatus?: EditResolution;
  onOpenEdit?: (annotation: EditAnnotation) => void;
  onResolved?: (annotation: EditAnnotation, status: Exclude<EditResolution, "pending">) => void;
}

export function EditCard({ annotation, filename, resolvedStatus, onOpenEdit, onResolved }: EditCardProps) {
  const [status, setStatus] = useState<EditResolution>(resolvedStatus ?? annotation.status ?? "pending");
  const [pendingAction, setPendingAction] = useState<"accept" | "reject" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const effectiveStatus = resolvedStatus ?? status;

  const resolve = async (action: "accept" | "reject") => {
    if (effectiveStatus !== "pending" || pendingAction) {
      return;
    }

    setPendingAction(action);
    setError(null);
    const revert = applyOptimisticResolution(annotation, action);
    const nextStatus = action === "accept" ? "accepted" : "rejected";
    setStatus(nextStatus);

    try {
      if (action === "accept") {
        await api.documents.acceptEdit(annotation.document_id, annotation.edit_id);
      } else {
        await api.documents.rejectEdit(annotation.document_id, annotation.edit_id);
      }
      invalidateDocxBytes(annotation.document_id);
      invalidatePdfBytes(annotation.document_id);
      onResolved?.(annotation, nextStatus);
    } catch (resolveError) {
      revert();
      setStatus("pending");
      setError(resolveError instanceof Error ? resolveError.message : "Could not update the edit.");
    } finally {
      setPendingAction(null);
    }
  };

  return (
    <div className="rounded-md border bg-card p-3 shadow-sm">
      <div className="flex flex-wrap items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium uppercase text-muted-foreground">{filename ?? "Tracked change"}</p>
          {annotation.reason ? <p className="mt-1 text-sm">{annotation.reason}</p> : null}
        </div>
        <StatusPill variant={effectiveStatus === "accepted" ? "success" : effectiveStatus === "rejected" ? "destructive" : "neutral"}>
          {effectiveStatus}
        </StatusPill>
      </div>

      <div className="mt-3 space-y-2 rounded-md bg-muted/40 p-3 text-sm leading-6">
        {annotation.deleted_text ? <p className="text-deletion line-through decoration-deletion">{annotation.deleted_text}</p> : null}
        {annotation.inserted_text ? <p className="text-insertion underline decoration-insertion underline-offset-2">{annotation.inserted_text}</p> : <p className="text-muted-foreground">No replacement text.</p>}
      </div>

      {error ? <p className="mt-2 text-xs text-destructive">{error}</p> : null}

      <div className="mt-3 flex flex-wrap justify-end gap-2">
        {onOpenEdit ? (
          <Button type="button" variant="outline" size="sm" onClick={() => onOpenEdit(annotation)}>
            View
          </Button>
        ) : null}
        <Button type="button" variant="outline" size="sm" disabled={effectiveStatus !== "pending" || Boolean(pendingAction)} onClick={() => void resolve("reject")}>
          {pendingAction === "reject" ? <Loader2 className="size-4 animate-spin" /> : <X className="size-4" />}
          Reject
        </Button>
        <Button type="button" size="sm" disabled={effectiveStatus !== "pending" || Boolean(pendingAction)} onClick={() => void resolve("accept")}>
          {pendingAction === "accept" ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />}
          Accept
        </Button>
        {effectiveStatus !== "pending" ? (
          <Button type="button" variant="ghost" size="sm" disabled>
            <RotateCcw className="size-4" />
            Resolved
          </Button>
        ) : null}
      </div>
    </div>
  );
}
